import { get } from 'api/apiClient';
import type { ApplicationType } from 'store/storageunits/types';
import type { UnitTypes } from 'unitData/types';

const BASE_PATH = 'suggestions';

export interface NodeSuggestion {
  type?: string;
  unitType?: string;
  probability: number;
  modelName?: string;
  concave?: string;
  mantle?: string;
  unitThrow?: number;
  variantId?: number;
}

export type NodeSuggestionsResponse =
  | NodeSuggestion[]
  | {
      suggestions: NodeSuggestion[];
    };

export interface GetNodeSuggestionsParams {
  unitType: UnitTypes;
  portIndex: number;
  application?: ApplicationType;
  take?: number;
  topSizeMm?: number;
  requiredCapacityTph?: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const optionalString = (value: unknown) =>
  typeof value === 'string' ? value : undefined;

const optionalNumber = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const toNodeSuggestion = (value: unknown): NodeSuggestion | null => {
  if (!isRecord(value)) {
    return null;
  }

  const probability = optionalNumber(value.probability);

  if (probability === undefined) {
    return null;
  }

  return {
    type: optionalString(value.type),
    unitType: optionalString(value.unitType),
    probability,
    modelName: optionalString(value.modelName),
    concave: optionalString(value.concave),
    mantle: optionalString(value.mantle),
    unitThrow: optionalNumber(value.unitThrow),
    variantId: optionalNumber(value.variantId),
  };
};

const getSuggestionsPayload = (response: unknown) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (isRecord(response) && Array.isArray(response.suggestions)) {
    return response.suggestions;
  }

  return [];
};

const getSuggestionsFromResponse = (response: unknown) =>
  getSuggestionsPayload(response).flatMap((suggestion) => {
    const nodeSuggestion = toNodeSuggestion(suggestion);

    return nodeSuggestion ? [nodeSuggestion] : [];
  });

export async function getNodeSuggestions(
  {
    unitType,
    portIndex,
    application,
    take,
    topSizeMm,
    requiredCapacityTph,
  }: GetNodeSuggestionsParams,
  signal?: AbortSignal,
) {
  const params = new URLSearchParams({
    unitType,
    portIndex: String(portIndex),
  });

  if (application) {
    params.set('application', application);
  }

  if (take !== undefined) {
    params.set('take', String(take));
  }

  if (topSizeMm !== undefined && Number.isFinite(topSizeMm)) {
    params.set('topSizeMm', String(topSizeMm));
  }
  if (
    requiredCapacityTph !== undefined &&
    Number.isFinite(requiredCapacityTph)
  ) {
    params.set('requiredCapacityTph', String(requiredCapacityTph));
  }

  const response = await get<NodeSuggestionsResponse | unknown>(
    `${BASE_PATH}?${params.toString()}`,
    signal ? { signal } : undefined,
  );

  return getSuggestionsFromResponse(response.parsedBody);
}
